import Vue from 'vue'
import App from './App.vue'
import router from './router'
import ElementUI from 'element-ui'
import 'element-ui/lib/theme-chalk/index.css'
import axios from 'axios'
import { clearAuthState, shouldAttemptSessionRestore } from './utils/userSession'
import { getAccessToken, clearAccessToken } from './utils/accessToken'
import { ensureAccessToken, AUTH_REFRESH_PATH, wasLastRefreshRejected } from './api/authRefresh'
import { startSessionKeepAlive } from './utils/sessionKeepAlive'

Vue.use(ElementUI)

axios.defaults.withCredentials = true

/** 请求拦截：内存 Access Token → Authorization（Refresh 等请求可设 skipAuth: true） */
axios.interceptors.request.use(
  (config) => {
    if (config.skipAuth) return config
    const t = getAccessToken()
    if (t) {
      config.headers = config.headers || {}
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${t}`
      }
    }
    return config
  },
  (err) => Promise.reject(err)
)

/** 后端统一响应：{ code, msg, data }；HTTP 2xx 且业务成功时业务数据在 data，否则用 msg 提示 */
function isApiBusinessSuccess(code) {
  const n = typeof code === 'string' ? Number(String(code).trim()) : Number(code)
  return !Number.isNaN(n) && n === 200
}

function isUnifiedApiEnvelope(body) {
  return (
    body !== null &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    !(typeof Blob !== 'undefined' && body instanceof Blob) &&
    Object.prototype.hasOwnProperty.call(body, 'code')
  )
}

function redirectToLoginIfNeeded() {
  const current = router.history.current
  if (current && current.path !== '/login') {
    router.push({ path: '/login', query: { redirect: current.fullPath } })
  }
}

axios.interceptors.response.use(
  (response) => {
    const body = response.data
    if (!isUnifiedApiEnvelope(body)) {
      return response
    }
    if (isApiBusinessSuccess(body.code)) {
      return { ...response, data: body.data }
    }
    const msg =
      body.msg != null && String(body.msg).trim() !== ''
        ? String(body.msg)
        : '请求失败'
    const err = new Error(msg)
    err.response = response
    err.isApiBusinessError = true
    return Promise.reject(err)
  },
  async (err) => {
    const d = err.response && err.response.data
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      const m = d.msg != null ? d.msg : d.message
      if (m != null && String(m).trim() !== '') {
        err.message = String(m)
      }
    }

    const status = err.response && err.response.status
    const config = err.config

    if (status === 401 && config && !config.skipAuth) {
      const url = String(config.url || '')
      if (url.includes(AUTH_REFRESH_PATH)) {
        clearAuthState()
        redirectToLoginIfNeeded()
        return Promise.reject(err)
      }
      if (config.__retryAfterAuth) {
        clearAuthState()
        redirectToLoginIfNeeded()
        return Promise.reject(err)
      }

      clearAccessToken()
      const ok = await ensureAccessToken()
      if (ok && getAccessToken()) {
        config.__retryAfterAuth = true
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${getAccessToken()}`
        return axios(config)
      }

      if (wasLastRefreshRejected()) {
        clearAuthState()
        redirectToLoginIfNeeded()
      } else {
        console.warn('认证服务暂时不可用，已保留持久登录状态，请稍后重试')
      }
      return Promise.reject(err)
    }

    return Promise.reject(err)
  }
)

Vue.config.productionTip = false

Vue.prototype.$bus = new Vue()

startSessionKeepAlive()

function mountApp() {
  new Vue({
    router,
    render: (h) => h(App)
  }).$mount('#app')
}

if (shouldAttemptSessionRestore()) {
  ensureAccessToken().finally(mountApp)
} else {
  mountApp()
}
